import type { MutableRefObject } from 'react';
import { useEffect, useRef } from 'react';
import * as c3 from 'c3';
import type { ChartAPI } from 'c3';
import { renderToString } from 'react-dom/server';
import { NPSChartTooltip } from '@hotjar/webapp/features/Surveys/Results/NPSLineChartWithResolution/NPSChartTooltip';
import type { NPSGraphResolution } from '@hotjar/webapp/features/Surveys/Results/NPSLineChartWithResolution/resolutions';
import { formatDateForResolution } from '@hotjar/webapp/features/Surveys/Results/NPSLineChartWithResolution/resolutions';
import { useTranslation } from '@hotjar/translations';

export interface NPSChartProps {
  npsScores?: string[];
  dateStrings?: string[];
  dateRangeInDays?: number;
  graphId?: string;
  resolution: NPSGraphResolution;
}

export function NPSChart({
  npsScores,
  dateStrings,
  dateRangeInDays = 0,
  resolution,
  graphId = 'nps-graph',
}: NPSChartProps): JSX.Element {
  const chart: MutableRefObject<ChartAPI | null> = useRef<ChartAPI | null>(null);
  const { i18n } = useTranslation();

  // this is a workaround to set a custom basis for an area chart (https://github.com/c3js/c3/issues/829)
  // eslint-disable-next-line
  (c3 as any).chart.internal.fn.getAreaBaseValue = () => -100;

  useEffect(() => {
    if (!npsScores || !dateStrings) return;

    chart.current = c3.generate({
      axis: {
        x: {
          type: 'timeseries',
          localtime: false,
          tick: {
            format: (d) => formatDateForResolution(d as Date, resolution),
          },
        },
        y: {
          max: 100,
          min: -100,
          padding: {
            bottom: 0,
            top: 5,
          },
          tick: {
            format: (d) => d.toLocaleString(i18n.language),
          },
        },
      },
      area: {
        zerobased: false,
      },
      bindto: `#${graphId}`,
      data: {
        x: 'x',
        columns: [['x'].concat(dateStrings), ['NPS Score'].concat(npsScores)],
        types: {
          'NPS Score': 'area',
        },
      },
      grid: {
        y: {
          show: true,
        },
      },
      legend: {
        show: false,
      },
      padding: {
        left: 30,
        right: 20,
        bottom: 10,
      },
      tooltip: {
        grouped: true,
        contents: (d) =>
          renderToString(
            <NPSChartTooltip
              sampleDate={d[0].x}
              score={d[0].value}
              resolution={resolution}
              dateRangeInDays={dateRangeInDays}
            />,
          ),
      },
    });
  }, [npsScores, dateStrings, dateRangeInDays, graphId, resolution, i18n.language]);

  return <div id={graphId} />;
}
